/**
 * Renderer for a CustomCreatureSim. Loads the user-uploaded GLB once and
 * clones it per instance (via SkeletonUtils so skinned meshes work). If the
 * GLB has animation clips, plays the first one on each clone. Positions
 * come from the sim each frame.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import type { CustomCreatureSim } from '../sim/CustomCreature';
import type { Terrain } from '../game/Terrain';

interface Avatar {
  root: THREE.Group;
  mixer: THREE.AnimationMixer | null;
  action: THREE.AnimationAction | null;
}

export class CustomCreatureMesh {
  readonly group = new THREE.Group();
  private avatars: Avatar[] = [];
  private loaded = false;
  private readonly forward = new THREE.Vector3();
  private readonly up = new THREE.Vector3();

  constructor(private sim: CustomCreatureSim, private terrain: Terrain) {
    this.group.name = `custom-creature-${sim.cfg.id}`;
    void this.load();
  }

  private async load(): Promise<void> {
    let gltf;
    try {
      gltf = await new GLTFLoader().loadAsync(this.sim.cfg.glbUrl);
    } catch (err) {
      console.warn(`[CustomCreature ${this.sim.cfg.id}] GLB load failed:`, err);
      return;
    }

    const src = gltf.scene;
    const scale = this.sim.cfg.scale;
    const rotX = this.sim.cfg.rotX ?? 0;
    const rotY = this.sim.cfg.rotY ?? 0;
    const rotZ = this.sim.cfg.rotZ ?? 0;
    src.rotation.set(rotX, rotY, rotZ);
    src.scale.setScalar(scale);
    src.updateMatrixWorld(true);

    // Sit the model on the ground: measure its bbox AFTER the scale, then
    // offset so the min-y sits at 0 (children will get terrain-sampled Y).
    // User-supplied offY nudges the model up/down after auto-levelling.
    const bb = new THREE.Box3().setFromObject(src);
    const yOffset = -bb.min.y + (this.sim.cfg.offY ?? 0);

    const clip = gltf.animations[0] ?? null;

    for (let i = 0; i < this.sim.instances.length; i++) {
      this.avatars.push(this.buildAvatar(src, clip, yOffset));
    }
    this.loaded = true;
  }

  private buildAvatar(src: THREE.Object3D, clip: THREE.AnimationClip | null, yOffset: number): Avatar {
    const root = new THREE.Group();
    root.matrixAutoUpdate = false;

    const cloned = skeletonClone(src);
    cloned.position.y = yOffset;
    cloned.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      m.castShadow = true;
      m.receiveShadow = true;
      m.frustumCulled = false;
    });
    root.add(cloned);

    let mixer: THREE.AnimationMixer | null = null;
    let action: THREE.AnimationAction | null = null;
    if (clip) {
      mixer = new THREE.AnimationMixer(cloned);
      action = mixer.clipAction(clip);
      action.play();
      action.time = Math.random() * clip.duration;
    }

    this.group.add(root);
    return { root, mixer, action };
  }

  update(dt: number): void {
    if (!this.loaded) return;
    // Keep avatar count in sync with instance count (sim.syncCount may have
    // resized the array after a config edit).
    while (this.avatars.length < this.sim.instances.length) {
      const first = this.avatars[0];
      if (!first) break;
      const cloned = first.root.children[0];
      if (!cloned) break;
      // Cheap: rebuild from the original source isn't available here, so
      // re-clone the first avatar's internal mesh. That shares materials
      // but is fine for runtime growth.
      const src = cloned;
      const root = new THREE.Group();
      root.matrixAutoUpdate = false;
      const re = skeletonClone(src);
      re.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh) return;
        m.castShadow = true;
        m.receiveShadow = true;
        m.frustumCulled = false;
      });
      root.add(re);
      this.group.add(root);
      this.avatars.push({ root, mixer: null, action: null });
    }
    while (this.avatars.length > this.sim.instances.length) {
      const av = this.avatars.pop();
      if (av) this.group.remove(av.root);
    }

    const instances = this.sim.instances;
    for (let i = 0; i < instances.length; i++) {
      const c = instances[i];
      const av = this.avatars[i];
      if (!av) continue;
      if (!c.alive) { av.root.visible = false; continue; }
      av.root.visible = true;
      this.writePose(av.root, c.x, c.y, c.z, c.heading);
      if (av.action) {
        const stationary = c.speed < 0.02;
        av.action.timeScale = stationary ? 0.25 : 1;
      }
      av.mixer?.update(dt);
    }
  }

  private writePose(root: THREE.Group, x: number, y: number, z: number, heading: number): void {
    this.terrain.getNormal(x, z, this.up);
    this.forward.set(Math.cos(heading), 0, Math.sin(heading));
    this.forward.addScaledVector(this.up, -this.forward.dot(this.up));
    if (this.forward.lengthSq() < 1e-6) {
      this.forward.set(1, 0, 0);
      this.forward.addScaledVector(this.up, -this.forward.dot(this.up));
    }
    this.forward.normalize();
    const bx = this.forward.y * this.up.z - this.forward.z * this.up.y;
    const by = this.forward.z * this.up.x - this.forward.x * this.up.z;
    const bz = this.forward.x * this.up.y - this.forward.y * this.up.x;
    const e = root.matrix.elements;
    e[0] = this.forward.x; e[1] = this.forward.y; e[2] = this.forward.z; e[3] = 0;
    e[4] = this.up.x;      e[5] = this.up.y;      e[6] = this.up.z;      e[7] = 0;
    e[8] = bx;             e[9] = by;             e[10] = bz;            e[11] = 0;
    e[12] = x;             e[13] = y;             e[14] = z;             e[15] = 1;
    root.matrixWorldNeedsUpdate = true;
  }
}
